import React from 'react';
import { TabContentSkeleton } from './TabContentSkeleton';

interface StudentProfileSkeletonProps {
  /**
   * Number of tabs to display in the skeleton
   */
  tabCount?: number;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * StudentProfileSkeleton component
 *
 * This component provides a loading skeleton specifically designed for student profile pages.
 * It includes a profile header, tab navigation, and tab content.
 *
 * @example
 * <StudentProfileSkeleton />
 */
export const StudentProfileSkeleton: React.FC<StudentProfileSkeletonProps> = ({
  tabCount = 6,
  className = '',
}) => {
  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1600px] mx-auto p-6 space-y-8">
        {/* Profile Header Skeleton */}
        <div className="flex flex-wrap justify-between items-start animate-pulse">
          <div className="flex flex-wrap items-center gap-6">
            {/* Profile Image */}
            <div className="w-24 h-24 rounded-full bg-gray-200"></div>

            {/* Profile Info */}
            <div>
              <div className="h-10 w-64 bg-gray-200 rounded-md mb-2"></div>
              <div className="flex flex-wrap items-center gap-4 mt-2">
                <div className="h-5 w-24 bg-gray-100 rounded-md"></div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.5em] text-gray-300">•</span>
                  <div className="h-5 w-32 bg-gray-100 rounded-md"></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.5em] text-gray-300">•</span>
                  <div className="h-5 w-40 bg-gray-100 rounded-md"></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[0.5em] text-gray-300">•</span>
                  <div className="h-8 w-32 bg-gray-100 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-6">
            <div className="h-10 w-32 bg-gray-200 rounded-xl"></div>
          </div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="relative">
          <div className="flex items-center overflow-x-auto pb-1 border-b border-gray-100">
            {[...Array(tabCount)].map((_, index) => (
              <div
                key={index}
                className="h-6 bg-gray-200 rounded-md animate-pulse"
                style={{
                  width: `${80 + (index % 3) * 10}px`,
                  animationDelay: `${index * 0.1}s`,
                }}
              ></div>
            ))}
          </div>
        </div>

        {/* Tab Content Skeleton - using the existing TabContentSkeleton component */}
        <TabContentSkeleton
          sectionCount={2}
          rowsPerSection={6}
          withSidebar={true}
        />
      </div>
    </div>
  );
};
